// Modals.css is no longer used here, but this is the only file that loads it and other
// (not yet migrated) modals still rely on its global .modal-btn-group/.spas-title/.save-buttons.
import "./Modals.css";
import React, {useState, useEffect} from "react";
import { deregisterSpaUser, editSpa, fetchSpa, registerSpaUser } from "../../../redux/actions/spa.actions";
import { AppDispatch } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import UserSelect from "../UserSelect/UserSelect";
import { ModalProps } from "../Interfaces/Modals";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import ModalShell, { ModalLoading } from "./ModalShell";
import { clearError } from "../../../redux/reducers/spa.reducer";
import { SERIAL_LENGTH } from "../../../pages/SpaDetails/components/EditSerialModal";

const EditSpaModal: React.FC<ModalProps> = ({open, setOpen, object_id}) => {
    const id = object_id;
    
    const dispatch: AppDispatch = useDispatch();
    const [updateInfo, setUpdateInfo] = useState<any>(null);
    const [selectedUser, setSelectedUser] = useState<any>(null);

    const {canAccess:canEditUser} = usePermissionCheck(['register_to_user','deregister_from_user']);

    const {data:spa, loading: spaLoading, error} = useSelector((state: RootState)=>state.spa)

    const [submitted, setSubmitted] = useState(false);
    // editSpa doesn't set the slice's loading flag, so track the save here.
    const [updating, setUpdating] = useState(false);
    const loading = updating || spaLoading;

    useEffect(() => {
        if (id && open){
            dispatch(fetchSpa(id));
        }
    }, [id,open, dispatch]);

    useEffect(() => {
        if (spa?.user) {
            setSelectedUser(spa.user);
        }
    }, [spa]);

    const handleClose = () => {
        dispatch(clearError());
        setUpdateInfo(null);
        setSelectedUser(null);
        setSubmitted(false);
        setOpen(false);
    };

    /* Handle text input */
    const handleInput = (e: any) => {
        let input = e.target.value;
        setUpdateInfo({...updateInfo,nick_name:input})
    };

    /* Handle serial input: digits only, at most 6 */
    const handleSerialInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        let input = e.target.value.replace(/\D/g, '').slice(0, SERIAL_LENGTH);
        setUpdateInfo({...updateInfo,arctic_serial_number:input})
    };

    const handleEdit = async () => {
        setSubmitted(true);
        setUpdating(true);
        let ok = true;
        if (updateInfo){
            const result = await dispatch(editSpa({...updateInfo,guid:spa?.id}));
            ok = editSpa.fulfilled.match(result);
        }

        if(selectedUser && ok){
            if (selectedUser!=='none'){
                await dispatch(registerSpaUser({guid:spa?.id, user_id:selectedUser.id}));
            } else {
                await dispatch(deregisterSpaUser({guid:spa?.id, user_id:selectedUser.id}));
            } 
        }
        setUpdating(false);
    }

  const busy = Boolean(loading) && submitted && !error;
  const done = Boolean(error) || submitted;

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      size={done ? "sm" : "md"}
      busy={busy}
      tone={error ? "error" : busy ? undefined : submitted ? "success" : undefined}
      title={
        error ? "An error occurred"
        : busy ? "Updating…"
        : loading ? "Loading…"
        : submitted ? "Spa updated"
        : <>Edit spa <strong>{spa?.nick_name}</strong></>
      }
      actions={busy ? undefined : done ?
        <button className="btn btn-primary" onClick={handleClose}>OK</button>
        :
        <>
          <button className="btn" onClick={handleClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleEdit} disabled={loading || (!updateInfo && !selectedUser)}>Confirm</button>
        </>
      }
    >
      {error ?
        <p>{error}</p>
      : loading ?
        <ModalLoading/>
      : submitted ?
        <p>Spa successfully updated.</p>
      :
        <>
          <div className="field">
            <label className="field-label" htmlFor="nick-name-input">Nickname</label>
            <input type="text" id="nick-name-input" className="input" placeholder="Enter new nickname" defaultValue={spa?.nick_name} onChange={handleInput}/>
          </div>

          {!spa?.blue_falls_serial_number &&
            <div className="field">
              <label className="field-label" htmlFor="serial-input">Serial number</label>
              <input
                type="text"
                id="serial-input"
                className="input"
                inputMode="numeric"
                maxLength={SERIAL_LENGTH}
                placeholder="Enter serial number"
                value={updateInfo?.arctic_serial_number ?? spa?.arctic_serial_number ?? ''}
                onChange={handleSerialInput}
              />
            </div>}

          {canEditUser &&
            <div className="dlg-section">
              <div className="dlg-current">
                <span className="dlg-label">Current user</span>
                <strong>{spa?.user ? spa?.user?.name : `None`}</strong>
              </div>
              <UserSelect setSelectedUser={setSelectedUser} currentUser={selectedUser || spa?.user}/>
            </div>}
        </>
      }
    </ModalShell>
  );
};

export default EditSpaModal;
